import React, { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';

interface Organization {
	id: string;
	name: string;
	currency_code: string;
	is_default_org: boolean;
}

interface ZohoOrganizationModalProps {
	isOpen: boolean;
	onClose: () => void;
	clientId: string;
	currentOrgId?: string | undefined;
}

function getAuthHeader(): Record<string, string> {
	const token = localStorage.getItem('adminToken');
	return token ? { Authorization: `Bearer ${token}` } : {};
}

export const ZohoOrganizationModal: React.FC<ZohoOrganizationModalProps> = ({
	isOpen,
	onClose,
	clientId,
	currentOrgId,
}) => {
	const [organizations, setOrganizations] = useState<Organization[]>([]);
	const [loading, setLoading] = useState(false);
	const [switching, setSwitching] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [detectedCurrentOrgId, setDetectedCurrentOrgId] = useState<string | null>(null);

	useEffect(() => {
		if (isOpen) {
			fetchOrganizations();
		}
	}, [isOpen]);

	const fetchOrganizations = async () => {
		setLoading(true);
		setError(null);

		try {
			const [orgsResponse, configResponse] = await Promise.all([
				fetch(`/api/auth/zoho/organizations/${clientId}`, { headers: getAuthHeader() }),
				fetch(`/api/auth/zoho/config/${clientId}`, { headers: getAuthHeader() }),
			]);

			const orgsData = await orgsResponse.json();
			const configData = await configResponse.json();

			if (orgsData.success) {
				setOrganizations(orgsData.organizations);
			} else {
				setError(orgsData.error || 'Failed to fetch organizations');
				return;
			}
			if (configData.success && configData.config.organizationId) {
				setDetectedCurrentOrgId(configData.config.organizationId);
			} else if (!currentOrgId && orgsData.organizations.length > 0) {
				const defaultOrg = orgsData.organizations.find((org: Organization) => org.is_default_org);
				if (defaultOrg) setDetectedCurrentOrgId(defaultOrg.id);
			}
		} catch {
			setError('Network error occurred');
		} finally {
			setLoading(false);
		}
	};

	const handleSwitchOrganization = async (organizationId: string) => {
		setSwitching(true);
		setError(null);

		try {
			const response = await fetch(`/api/auth/zoho/switch-organization/${clientId}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...getAuthHeader(),
				},
				body: JSON.stringify({ organizationId }),
			});

			const data = await response.json();

			if (data.success) {
				setTimeout(() => {
					onClose();
					window.location.reload();
				}, 1000);
			} else {
				setError(data.error || 'Failed to switch organization');
			}
		} catch {
			setError('Network error occurred');
		} finally {
			setSwitching(false);
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
			<div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-2 sm:mx-4 max-h-[90vh] flex flex-col">
				<div className="flex-shrink-0 flex items-center justify-between p-5 border-b border-gray-100">
					<div>
						<h3 className="text-base font-semibold text-gray-900">Switch Zoho Organization</h3>
						<p className="text-xs text-gray-500 mt-0.5">Select the organization to sync with</p>
					</div>
					<button
						onClick={onClose}
						className="text-gray-400 hover:text-gray-600 transition-colors p-1"
						disabled={switching}>
						<X className="h-5 w-5" />
					</button>
				</div>

				<div className="flex-1 min-h-0 overflow-y-auto p-5 relative">
					{error && (
						<div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
							<p className="text-sm text-red-600">{error}</p>
						</div>
					)}

					{loading ? (
						<div className="flex items-center justify-center py-10">
							<div className="text-center">
								<RefreshCw className="animate-spin h-6 w-6 text-blue-600 mx-auto mb-2" />
								<p className="text-sm text-gray-500">Loading organizations...</p>
							</div>
						</div>
					) : organizations.length > 0 ? (
						<div className="flex flex-col gap-2">
							{organizations.map((org) => {
								const isCurrentOrg = currentOrgId === org.id || detectedCurrentOrgId === org.id;
								return (
									<button
										key={org.id}
										onClick={() => handleSwitchOrganization(org.id)}
										disabled={switching || isCurrentOrg}
										className={`w-full p-3 text-left rounded-lg border-2 transition-all ${
											isCurrentOrg
												? 'border-green-400 bg-green-50 cursor-default'
												: 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
										} disabled:opacity-50`}>
										<div className="flex items-center justify-between">
											<div>
												<span className={`text-sm font-medium ${isCurrentOrg ? 'text-green-800' : 'text-gray-900'}`}>
													{org.name}
												</span>
												<span className="ml-2 text-xs text-gray-500">{org.currency_code}</span>
												{org.is_default_org && !isCurrentOrg && (
													<span className="ml-2 text-xs text-blue-600">Default</span>
												)}
											</div>
											{isCurrentOrg && (
												<span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
													Active
												</span>
											)}
										</div>
									</button>
								);
							})}
						</div>
					) : (
						<div className="text-center py-10">
							<p className="text-sm text-gray-500">No organizations found</p>
						</div>
					)}

					{switching && (
						<div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-xl">
							<div className="text-center">
								<RefreshCw className="animate-spin h-6 w-6 text-blue-600 mx-auto mb-2" />
								<p className="text-sm text-gray-700">Switching organization...</p>
							</div>
						</div>
					)}
				</div>

				<div className="flex-shrink-0 px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
					<button
						onClick={onClose}
						disabled={switching}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all disabled:opacity-50">
						Cancel
					</button>
				</div>
			</div>
		</div>
	);
};
